import { useTranslation } from 'react-i18next';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { localizedName } from '@/components/localized-name';
import { formatCurrency } from '@/lib/format';
import { cn } from '@/lib/utils';
import { useEventPackages } from './events.api';
import type { EventPackage } from './types';

/**
 * The packages an event is currently selling, as a choice.
 *
 * Used by all three places a package is picked — creating a booking from the
 * sales page, creating one from a time slot, and moving an existing booking
 * between packages. An event with no packages renders nothing at all, which is
 * what keeps unpackaged events looking exactly as they did.
 */
export function PackagePicker({
  eventId,
  value,
  onChange,
  /** Omit the package the booking is already on, when moving between them. */
  excludeId,
  label,
  disabled,
}: {
  eventId: string | null | undefined;
  value: string | null;
  onChange: (id: string, chosen: EventPackage) => void;
  excludeId?: string | null;
  label?: string;
  disabled?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? 'en';

  // Only what is on sale: nothing new is ever sold onto a retired package.
  const packages = useEventPackages(eventId, false);

  const options = (packages.data ?? []).filter(
    (option) => option.id !== excludeId
  );

  // Nothing to say about an event that sells at its own price.
  if (packages.isSuccess && options.length === 0 && !excludeId) return null;

  return (
    <div className="space-y-1.5">
      {label && <Label className="text-xs font-semibold">{label}</Label>}

      {/* `isLoading`, not `isPending`: with no event the query never runs,
          and a pending one would show the placeholder forever. */}
      {packages.isLoading && (
        <div className="space-y-1">
          {Array.from({ length: 2 }).map((_, index) => (
            <Skeleton key={index} className="h-12 w-full rounded-lg" />
          ))}
        </div>
      )}

      {packages.isError && (
        <p className="text-muted-foreground py-2 text-xs">
          {t('events.packagesFailed')}
        </p>
      )}

      {packages.isSuccess && options.length === 0 && (
        <p className="text-muted-foreground py-2 text-center text-xs">
          {t('events.noOtherPackages')}
        </p>
      )}

      <div className="max-h-56 space-y-1 overflow-y-auto">
        {options.map((option) => (
          <button
            key={option.id}
            type="button"
            disabled={disabled}
            onClick={() => onChange(option.id, option)}
            className={cn(
              'w-full rounded-lg border p-2 text-left text-sm transition-colors',
              disabled ? 'cursor-not-allowed opacity-50' : 'hover:bg-accent',
              value === option.id && 'border-primary bg-accent'
            )}
          >
            <p className="font-medium">
              {localizedName(option, language)}
              <span className="ml-1.5 font-normal tabular-nums">
                {formatCurrency(option.price)}
              </span>
            </p>
            {(language === 'en'
              ? option.descriptionEn
              : option.descriptionEs) && (
              <p className="text-muted-foreground text-xs">
                {language === 'en'
                  ? option.descriptionEn
                  : option.descriptionEs}
              </p>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}